import { useEffect, useMemo, useState } from 'react'
import { useInRouterContext, useSearchParams, type SetURLSearchParams } from 'react-router-dom'
import { ProductCard } from '@/storefront/components/ProductCard'
import { ShopSortToolbar, parseShopSortParam, parseShopViewParam } from '@/storefront/components/ShopSortToolbar'
import { StorefrontSection } from '@/storefront/components/StorefrontSection'
import { PreviewInlineText } from '@/storefront/components/PreviewInlineText'
import { EmptyState } from '@/storefront/components/EmptyState'
import { LoadingState } from '@/storefront/components/LoadingState'
import { useReferenceStore } from '@/storefront/context/ReferenceStoreContext'
import { useStorefrontBase } from '@/storefront/hooks/useStorefrontBase'
import { useReferenceProducts } from '@/storefront/hooks/useReferenceCatalog'
import { getShopProductBadge } from '@/storefront/lib/shopBadges'
import {
  getEffectiveProductPrice,
  isCatalogProductInStock,
  sortProductsByShopMode,
} from '@/storefront/lib/shopCatalogSort'
import {
  parseMoneyQueryParam,
  readInStockOnlyFromSearchParams,
} from '@/storefront/lib/shopUrlQueryParams'
import { resolveProductCardListingUx } from '@/storefront/lib/productCardListingUx'
import type { Product } from '@/services/products'

/** Stable empty params when rendered outside a Router (e.g. drag overlay). */
const EMPTY_SEARCH_PARAMS = new URLSearchParams()

function parseRatingParam(s: string | null): number | null {
  if (s == null || s.trim() === '') return null
  const n = parseInt(s, 10)
  if (!Number.isFinite(n) || n < 1 || n > 5) return null
  return n
}

function applyPopularityFilter(products: Product[], stars: number | null): Product[] {
  if (stars == null || stars < 2 || products.length === 0) return products
  const sortedCounts = products.map((p) => p.orders_count ?? 0).sort((a, b) => a - b)
  const qIdx = Math.min(3, Math.max(0, stars - 2))
  const frac = [0.2, 0.4, 0.6, 0.8][qIdx] ?? 0
  const idx = Math.min(
    sortedCounts.length - 1,
    Math.max(0, Math.floor(frac * (sortedCounts.length - 1)))
  )
  const threshold = sortedCounts[idx] ?? 0
  return products.filter((p) => (p.orders_count ?? 0) >= threshold)
}

const COLS_CLASS: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  5: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
  6: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6',
}

export type ProductGridBlockProps = {
  title?: string
  subtitle?: string
  limit?: number
  columns?: number
  anchorId?: string
  showQuickAddOnCards?: boolean
  /** Show sort + view toggle (uses URL `sort` + `view`). Default true when Router is available. */
  showShopToolbar?: boolean
  showVariantSwatches?: boolean
  showStockIndicator?: boolean
  merchBadges?: boolean
  imageHover?: boolean
}

function ProductGridRouted(props: ProductGridBlockProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  return (
    <ProductGridContent
      {...props}
      searchParams={searchParams}
      setSearchParams={setSearchParams}
    />
  )
}

function ProductGridContent(
  props: ProductGridBlockProps & {
    searchParams: URLSearchParams
    setSearchParams?: SetURLSearchParams
  }
) {
  const { store } = useReferenceStore()
  const base = useStorefrontBase()
  const q = useReferenceProducts(store.id)
  const { searchParams } = props

  const MAX_RENDER = 96
  const PAGE_SIZE = typeof props.limit === 'number' && props.limit > 0
    ? Math.min(props.limit, MAX_RENDER)
    : 24

  const columnsRaw = typeof props.columns === 'string' ? parseInt(props.columns, 10) : (props.columns ?? 4)
  const columns = Number.isFinite(columnsRaw) && columnsRaw >= 2 ? Math.min(columnsRaw, 6) : 4
  const anchorId = typeof props.anchorId === 'string' && props.anchorId.trim() ? props.anchorId.trim() : 'shop-products'
  const quickAddCards = props.showQuickAddOnCards === true
  const showToolbar =
    props.showShopToolbar !== false && typeof props.setSearchParams === 'function'
  const cardUx = resolveProductCardListingUx({
    showVariantSwatches: props.showVariantSwatches,
    showStockIndicator: props.showStockIndicator,
    merchBadges: props.merchBadges,
    imageHover: props.imageHover,
  })

  const [page, setPage] = useState(1)

  const filterKey = searchParams.toString()
  useEffect(() => {
    setPage(1)
  }, [filterKey])

  const allProducts = q.data ?? []

  const filteredProducts = useMemo(() => {
    let list = allProducts
    const cat = searchParams.get('category_id')
    if (cat && cat.trim() !== '') {
      list = list.filter((p) => p.category_id === cat)
    }
    const minP = parseMoneyQueryParam(searchParams.get('min_price'))
    const maxP =
      parseMoneyQueryParam(searchParams.get('max_price')) ??
      parseMoneyQueryParam(searchParams.get('maxPrice'))
    if (minP != null) list = list.filter((p) => getEffectiveProductPrice(p) >= minP)
    if (maxP != null) list = list.filter((p) => getEffectiveProductPrice(p) <= maxP)
    if (readInStockOnlyFromSearchParams(searchParams)) {
      list = list.filter(isCatalogProductInStock)
    }
    const stars = parseRatingParam(searchParams.get('min_rating'))
    list = applyPopularityFilter(list, stars)
    return list
  }, [allProducts, searchParams])

  const sortMode = parseShopSortParam(searchParams.get('sort'))
  const viewMode = parseShopViewParam(searchParams.get('view'))

  const sortedProducts = useMemo(
    () => sortProductsByShopMode(filteredProducts, sortMode),
    [filteredProducts, sortMode]
  )

  const hasActiveFilters = useMemo(() => {
    const cat = searchParams.get('category_id')
    if (cat && cat.trim() !== '') return true
    if (parseMoneyQueryParam(searchParams.get('min_price')) != null) return true
    if (
      parseMoneyQueryParam(searchParams.get('max_price')) != null ||
      parseMoneyQueryParam(searchParams.get('maxPrice')) != null
    )
      return true
    if (readInStockOnlyFromSearchParams(searchParams)) return true
    if (parseRatingParam(searchParams.get('min_rating')) != null) return true
    if (searchParams.get('sort') && searchParams.get('sort') !== 'featured') return true
    if (searchParams.get('view') === 'list') return true
    return false
  }, [searchParams])

  if (q.isLoading) return <LoadingState label="Chargement des produits…" />
  if (q.isError) return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-red-200 bg-red-50/60 px-6 py-10 text-center">
        <p className="text-sm font-semibold text-red-800">Impossible de charger les produits</p>
        <button type="button" onClick={() => q.refetch()} className="text-xs font-semibold text-red-700 underline-offset-2 hover:underline">Réessayer</button>
      </div>
    </div>
  )
  const shown = sortedProducts.slice(0, Math.min(PAGE_SIZE * page, MAX_RENDER))
  const hasMore = shown.length < sortedProducts.length && shown.length < MAX_RENDER

  const colsClass = COLS_CLASS[columns] ?? COLS_CLASS[4]
  const listUlClass = viewMode === 'list' ? 'flex flex-col gap-4' : `grid gap-4 sm:gap-6 ${colsClass}`

  return (
    <StorefrontSection
      as="section"
      id={anchorId}
      width="boxed"
      padding="lg"
      surface="transparent"
      className="scroll-mt-[4.5rem]"
    >
        <div className="mb-8">
          <p className="ref-label">Catalog</p>
          <h2 className="mt-2 text-2xl font-bold tracking-tight text-ref-text sm:text-[1.75rem]">
            <PreviewInlineText
              propKey="title"
              value={typeof props.title === 'string' ? props.title : ''}
              as="span"
              placeholder="Titre de section"
            />
          </h2>
          {props.subtitle ? (
            <p className="mt-2 text-base text-muted-foreground">
              <PreviewInlineText
                propKey="subtitle"
                value={props.subtitle}
                as="span"
                placeholder="Sous-titre de section"
              />
            </p>
          ) : null}
          <p className="mt-1 text-sm text-muted-foreground">
            {hasActiveFilters
              ? `${shown.length} of ${sortedProducts.length} matching${sortedProducts.length === 1 ? ' item' : ' items'}`
              : `${shown.length} of ${allProducts.length} ${allProducts.length === 1 ? 'item' : 'items'}`}
          </p>
        </div>

        {showToolbar && props.setSearchParams ? (
          <ShopSortToolbar searchParams={searchParams} setSearchParams={props.setSearchParams} />
        ) : null}

        {allProducts.length === 0 ? (
          <EmptyState title="Aucun produit pour le moment" description="Cette boutique n’a pas encore de produits publiés." />
        ) : sortedProducts.length === 0 ? (
          <EmptyState
            title="Aucune correspondance"
            description="Try adjusting filters or clearing them to see more products."
          />
        ) : (
          <>
            <ul className={listUlClass}>
              {shown.map((p) => (
                <li key={p.id}>
                  <ProductCard
                    product={p}
                    to={`${base}/product/${encodeURIComponent(p.slug || p.id)}`}
                    badge={getShopProductBadge(p)}
                    enableQuickAdd={quickAddCards}
                    {...cardUx}
                    cardLayout={viewMode === 'list' ? 'list' : 'grid'}
                  />
                </li>
              ))}
            </ul>

            {hasMore ? (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:border-border hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border"
                >
                  Load more
                  <span className="text-muted-foreground">
                    ({sortedProducts.length - shown.length} remaining)
                  </span>
                </button>
              </div>
            ) : null}
          </>
        )}
    </StorefrontSection>
  )
}

export function ProductGridBlock(props: ProductGridBlockProps) {
  const inRouter = useInRouterContext()
  if (!inRouter) {
    return <ProductGridContent {...props} searchParams={EMPTY_SEARCH_PARAMS} setSearchParams={undefined} />
  }
  return <ProductGridRouted {...props} />
}
